import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Download, Info } from 'lucide-react';

import { fbmAsnV2Api } from 'api/fbmAsnV2';
import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { cn } from 'shadcn/lib/utils';
import {
    BULK_UPLOAD_STATUS,
    BulkUploadDropzone,
    BulkUploadRowFilterSwitch,
    BulkUploadStatus,
    BulkUploadTable,
    useBulkUpload,
} from 'shared/components/bulkUpload';
import { BulkUploadPendingChanges } from 'shared/components/bulkUpload/BulkUploadPendingChanges';
import { useFbmItemsQuery } from 'shared/hooks/fbm/useFbmItemsQuery';

const ASN_COLUMN_DEFS = [
    { key: 'scheduledDeliveryDate', label: 'Scheduled Delivery Date', required: true },
    { key: 'scheduledDeliveryTime', label: 'Scheduled Delivery Time', required: true },
    { key: 'minTemp', label: 'Min Temp', required: false },
    { key: 'maxTemp', label: 'Max Temp', required: false },
    { key: 'shipper', label: 'Shipper', required: true },
    { key: 'facility', label: 'Facility', required: true },
    { key: 'specialInstructions', label: 'Special Instructions', required: false },
    { key: 'sku', label: 'SKU', required: true },
    { key: 'expectedExpirationDate', label: 'Expected Expiration Date', required: true },
    { key: 'partnerLotNumber', label: 'Partner Lot Number', required: false },
    { key: 'numOfCases', label: 'Num of Cases', required: true },
    { key: 'numberOfPallets', label: 'Number of pallets', required: false },
    { key: 'unitsPerCase', label: 'Units per Case', required: true },
    { key: 'requireInspectionUponReceipt', label: 'Require Inspection Upon Receipt', required: false },
];

/**
 * Bulk ASN Upload page for FBM.
 * Allows users to upload a CSV, view parsed data, and see validation errors.
 */
function BulkASNUpload() {
    const { fbmAccount } = useContext(UserContext);
    const fbmAccountName = fbmAccount?.fbmAccountName || '';

    const api = {
        edit: (key, params, rowsToUpdate, rowsToDelete) =>
            fbmAsnV2Api.bulkEdit(key, params.fbmAccountName, rowsToUpdate, rowsToDelete),
        fetch: (key, params, signal) => fbmAsnV2Api.bulkFetch(key, params.fbmAccountName, signal),
        persist: (key, params) => fbmAsnV2Api.bulkPersist(key, params.fbmAccountName),
        upload: (file, params) => fbmAsnV2Api.bulkUpload(file, params.fbmAccountName),
    };

    const { data: fbmItems = [] } = useFbmItemsQuery({ fbmAccountName });

    const unitsPerCaseBySku = useMemo(() => {
        const map = new Map();
        fbmItems.forEach((item) => {
            if (item.thirdPartyItemID && item.baseUnitsPerCase) {
                map.set(item.thirdPartyItemID, item.baseUnitsPerCase);
            }
        });
        return map;
    }, [fbmItems]);

    const isRowWithIssues = useCallback(
        ({ row }) => {
            const { sku, unitsPerCase: currentUnitsPerCase } = row.data;
            const needsUnitsPerCase =
                currentUnitsPerCase === null || currentUnitsPerCase === undefined || currentUnitsPerCase === '';
            return Boolean(sku && needsUnitsPerCase && !unitsPerCaseBySku.has(sku));
        },
        [unitsPerCaseBySku],
    );

    const bulkUpload = useBulkUpload({
        api,
        isRowWithIssues,
        params: { fbmAccountName },
    });

    const [persistSuccess, setPersistSuccess] = useState(false);
    const [prepopulatedCount, setPrepopulatedCount] = useState(0);
    const [hasUnknownSkus, setHasUnknownSkus] = useState(false);
    const hasAutoPopulatedRef = useRef(false);
    // Track which rows had unitsPerCase pre-populated (rowIndex -> value)
    const prepopulatedEditsRef = useRef(new Map());

    // Helper to reset auto-populate state
    const resetAutoPopulateState = useCallback((options = {}) => {
        const { resetCount = false } = options;
        hasAutoPopulatedRef.current = false;
        prepopulatedEditsRef.current = new Map();
        if (resetCount) {
            setPrepopulatedCount(0);
            setHasUnknownSkus(false);
        }
    }, []);

    useEffect(() => {
        if (
            bulkUpload.rows.length === 0 ||
            unitsPerCaseBySku.size === 0 ||
            hasAutoPopulatedRef.current ||
            bulkUpload.status !== BULK_UPLOAD_STATUS.READY
        ) {
            return;
        }

        const rowsToPopulate = [];
        let foundUnknownSku = false;

        bulkUpload.rows.forEach((row, index) => {
            const { sku, unitsPerCase: currentUnitsPerCase } = row.data;
            const needsUnitsPerCase =
                currentUnitsPerCase === null || currentUnitsPerCase === undefined || currentUnitsPerCase === '';

            if (needsUnitsPerCase && sku) {
                if (unitsPerCaseBySku.has(sku)) {
                    rowsToPopulate.push({
                        rowIndex: index,
                        value: unitsPerCaseBySku.get(sku),
                    });
                } else {
                    // SKU exists but not found in item master - potentially invalid
                    foundUnknownSku = true;
                }
            }
        });

        if (rowsToPopulate.length > 0) {
            // Store the pre-populated edits for later use in discard
            const newPrepopulatedEdits = new Map();
            rowsToPopulate.forEach(({ rowIndex, value }) => {
                bulkUpload.updateCell(rowIndex, 'unitsPerCase', String(value));
                newPrepopulatedEdits.set(rowIndex, String(value));
            });
            prepopulatedEditsRef.current = newPrepopulatedEdits;
            setPrepopulatedCount(rowsToPopulate.length);
        }

        setHasUnknownSkus(foundUnknownSku);
        hasAutoPopulatedRef.current = true;
    }, [bulkUpload.rows, bulkUpload.status, unitsPerCaseBySku, bulkUpload.updateCell]);

    // Reset the auto-populate flag when starting a new upload or submitting changes
    useEffect(() => {
        if (bulkUpload.status === BULK_UPLOAD_STATUS.IDLE) {
            resetAutoPopulateState({ resetCount: true });
        } else if (bulkUpload.status === BULK_UPLOAD_STATUS.SUBMITTING) {
            // Reset so auto-populate can run again after submit completes
            resetAutoPopulateState();
        }
    }, [bulkUpload.status, resetAutoPopulateState]);

    // Clear the prepopulated message when changes are submitted
    useEffect(() => {
        if (!bulkUpload.hasUnsavedChanges && prepopulatedCount > 0) {
            setPrepopulatedCount(0);
        }
    }, [bulkUpload.hasUnsavedChanges, prepopulatedCount]);

    // TODO: Move the getting of example data to the backend so there is one source of truth.
    const handleDownloadTemplate = useCallback(() => {
        const headers = ASN_COLUMN_DEFS.map((col) => `${col.label}${col.required ? '*' : ''}`).join(',');

        // Generate example dates
        const nextWeek = new Date();
        nextWeek.setDate(nextWeek.getDate() + 7);
        const sixMonthsLater = new Date();
        sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);

        // Get first SKU from fbmItems if available (check for truthy thirdPartyItemID)
        const exampleSku =
            fbmItems.length > 0 && fbmItems[0].thirdPartyItemID ? fbmItems[0].thirdPartyItemID : 'EXAMPLE-SKU';
        const exampleUnitsPerCase =
            fbmItems.length > 0 && fbmItems[0].baseUnitsPerCase ? fbmItems[0].baseUnitsPerCase : 12;

        // Build example row matching ASN_COLUMN_DEFS order
        const exampleRow = {
            scheduledDeliveryDate: dayjs(nextWeek).format('MM/DD/YY'),
            scheduledDeliveryTime: '09:00',
            minTemp: '32',
            maxTemp: '40',
            shipper: 'Example Shipper',
            facility: 'LAX1',
            specialInstructions: 'This is an instruction',
            sku: exampleSku,
            expectedExpirationDate: dayjs(sixMonthsLater).format('MM/DD/YY'),
            partnerLotNumber: 'LOT-001',
            numOfCases: '10',
            numberOfPallets: '1',
            unitsPerCase: String(exampleUnitsPerCase),
            requireInspectionUponReceipt: 'true/false/yes/no',
        };

        const exampleValues = ASN_COLUMN_DEFS.map((col) => exampleRow[col.key] ?? '').join(',');

        const csvContent = `${headers}\n${exampleValues}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'asn_bulk_upload_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [fbmItems]);

    const handlePersist = useCallback(async () => {
        try {
            const result = await bulkUpload.persist();
            // Only show full success if there are no persist errors
            const hasErrors = result?.persistResults?.errors?.length > 0;
            if (!hasErrors) {
                setPersistSuccess(true);
                bulkUpload.reset();
            }
            // If there are errors, they will be displayed via bulkUpload.persistErrors
        } catch {
            // Error is already captured in bulkUpload.persistError
        }
    }, [bulkUpload]);

    const handleStartNew = useCallback(() => {
        setPersistSuccess(false);
    }, []);

    // Custom discard handler that preserves pre-populated unitsPerCase edits
    const handleDiscardChanges = useCallback(() => {
        // Clear all pending changes first
        bulkUpload.clearPendingChanges();

        // Re-apply only the pre-populated unitsPerCase edits
        if (prepopulatedEditsRef.current.size > 0) {
            prepopulatedEditsRef.current.forEach((value, rowIndex) => {
                bulkUpload.updateCell(rowIndex, 'unitsPerCase', value);
            });
        }
    }, [bulkUpload]);

    const handleFileSelect = useCallback(
        (file) => {
            if (file && fbmAccountName) {
                bulkUpload.uploadFile(file);
            }
        },
        [bulkUpload, fbmAccountName],
    );

    const hasData = bulkUpload.rows.length > 0;
    const errorCount = bulkUpload.validationErrors.length;

    const infoMessages = useMemo(() => {
        const messages = [];
        if (prepopulatedCount > 0) {
            messages.push(
                `Units per case was pre-populated for ${prepopulatedCount} row${prepopulatedCount !== 1 ? 's' : ''} based on item master data. These values are shown as pending edits.`,
            );
        }
        return messages;
    }, [prepopulatedCount]);

    const warningMessages = useMemo(() => {
        const messages = [];
        if (hasUnknownSkus) {
            messages.push(
                'One or more rows have a SKU that was not found in the item master. These SKUs may be invalid.',
            );
        }
        return messages;
    }, [hasUnknownSkus]);

    return (
        <div className={cn('mt-4 flex flex-col space-y-6', hasData && 'min-h-[calc(100dvh-10rem)]')}>
            <Card className={cn(hasData && 'flex min-h-0 min-w-0 flex-1 flex-col')}>
                <CardHeader>
                    <CardTitle>Bulk ASN Upload</CardTitle>
                    <CardDescription>
                        Upload a CSV file to create multiple ASNs at once. The file will be validated before you can
                        save.
                        <br />
                        There is currently a limit of 1000 rows per bulk upload.
                        <br />{' '}
                        <b>
                            Multiple lines can be grouped as one ASN by keeping the following fields the same:
                            <br />
                            Scheduled Delivery Date, Scheduled Delivery Time, Min Temp, Max Temp, Shipper, Facility,
                            Special Instructions
                        </b>
                        <br />
                        <b>
                            Note: The Shipper field must reference a shipper that has already been added to the system.
                        </b>
                    </CardDescription>
                </CardHeader>
                <CardContent className={cn('space-y-4', hasData && 'flex min-h-0 min-w-0 flex-1 flex-col')}>
                    {/* Show dropzone if no data yet and not in success state */}
                    {!hasData && !persistSuccess && (
                        <>
                            <div className="flex justify-end">
                                <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                                    <Download className="mr-2 h-4 w-4" />
                                    Download Template
                                </Button>
                            </div>
                            <BulkUploadDropzone
                                onFileSelect={handleFileSelect}
                                disabled={
                                    !fbmAccountName ||
                                    [BULK_UPLOAD_STATUS.LOADING, BULK_UPLOAD_STATUS.UPLOADING].includes(
                                        bulkUpload.status,
                                    )
                                }
                                isUploading={bulkUpload.status === BULK_UPLOAD_STATUS.UPLOADING}
                            />
                        </>
                    )}

                    <BulkUploadStatus
                        entityName="ASN"
                        fetchError={bulkUpload.fetchError}
                        handleStartNew={handleStartNew}
                        hasData={hasData}
                        infoMessages={infoMessages}
                        persistError={bulkUpload.persistError}
                        persistSuccess={persistSuccess}
                        status={bulkUpload.status}
                        uploadError={bulkUpload.uploadError}
                        warningMessages={warningMessages}
                    />

                    {hasData &&
                        !persistSuccess &&
                        !bulkUpload.hasPersistErrors &&
                        ![BULK_UPLOAD_STATUS.SUBMITTING, BULK_UPLOAD_STATUS.PERSISTING].includes(bulkUpload.status) && (
                            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                        {errorCount > 0 ? (
                                            <Alert variant="destructive" className="inline-flex">
                                                {errorCount} validation error{errorCount !== 1 ? 's' : ''} found. Please
                                                fix the errors before saving. You can hover over the highlighted cells
                                                for more information.
                                            </Alert>
                                        ) : (
                                            <Alert className="inline-flex border-green-500 text-green-700">
                                                No validation errors. Ready to save.
                                            </Alert>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                            {bulkUpload.showIssuesFilterHint && (
                                                <Alert
                                                    className="w-full max-w-md border-sky-200 bg-sky-50 py-2 pr-3 text-sm text-sky-950 sm:w-auto dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-50"
                                                    data-testid="bulk-asn-filtered-view-hint"
                                                    role="status"
                                                >
                                                    <Info aria-hidden className="text-sky-600 dark:text-sky-400" />
                                                    <AlertDescription className="text-sky-900 dark:text-sky-100">
                                                        Showing only rows with validation issues, warnings, pending
                                                        edits, or unknown SKUs. Turn off the switch to see all rows.
                                                    </AlertDescription>
                                                </Alert>
                                            )}
                                            {bulkUpload.showIssuesRowFilter && (
                                                <BulkUploadRowFilterSwitch
                                                    checked={bulkUpload.showIssuesOnly}
                                                    disabled={bulkUpload.rows.length === 0}
                                                    onCheckedChange={bulkUpload.setShowIssuesOnly}
                                                />
                                            )}
                                        </div>
                                        {bulkUpload.hasUnsavedChanges && (
                                            <>
                                                <Button variant="outline" size="sm" onClick={handleDiscardChanges}>
                                                    Discard Changes
                                                </Button>
                                                <Button variant="default" size="sm" onClick={bulkUpload.submitChanges}>
                                                    Submit Changes
                                                </Button>
                                            </>
                                        )}
                                        <Button variant="outline" size="sm" onClick={bulkUpload.reset}>
                                            Start Over
                                        </Button>
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={handlePersist}
                                            disabled={!bulkUpload.canPersist}
                                        >
                                            Save ASNs
                                        </Button>
                                    </div>
                                </div>
                                <BulkUploadTable
                                    columnDefs={ASN_COLUMN_DEFS}
                                    fillAvailableHeight
                                    rows={bulkUpload.tableRows}
                                    errorsByCell={bulkUpload.errorsByCell}
                                    pendingEdits={bulkUpload.pendingEdits}
                                    updateCell={bulkUpload.updateCell}
                                    pendingDeletes={bulkUpload.pendingDeletes}
                                    toggleRowDelete={bulkUpload.toggleRowDelete}
                                />
                                <BulkUploadPendingChanges
                                    pendingDeletes={bulkUpload.pendingDeletes}
                                    pendingEdits={bulkUpload.pendingEdits}
                                    rowsLength={bulkUpload.rows.length}
                                />
                            </div>
                        )}

                    {/* Disabled state message */}
                    {!fbmAccountName && (
                        <p className="text-sm text-muted-foreground">
                            Please select an FBM account above to upload files.
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export { BulkASNUpload };
